import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const FooterSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    columns: [],
    social_links: { facebook: '', instagram: '', linkedin: '', youtube: '', tiktok: '', whatsapp: '' },
    copyright_text: '',
    company_text: '',
    powered_by_text: ''
  });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/footer-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      const s = data.settings || {};
      setFormData({
        columns: s.columns || [],
        social_links: s.social_links || { facebook: '', instagram: '', linkedin: '', youtube: '', tiktok: '', whatsapp: '' },
        copyright_text: s.copyright_text || '',
        company_text: s.company_text || '',
        powered_by_text: s.powered_by_text || ''
      });
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/footer-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save');
      alert('Footer settings saved successfully!');
    } catch (err) {
      alert('Failed to save footer settings');
    } finally {
      setSaving(false);
    }
  };

  // Column management
  const addColumn = () => {
    setFormData(prev => ({
      ...prev,
      columns: [...prev.columns, { title: '', links: [] }]
    }));
  };

  const removeColumn = (index) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.filter((_, i) => i !== index)
    }));
  };

  const updateColumnTitle = (index, title) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => i === index ? { ...col, title } : col)
    }));
  };

  // Link management
  const addLink = (colIndex) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === colIndex ? { ...col, links: [...col.links, { label: '', url: '' }] } : col
      )
    }));
  };

  const removeLink = (colIndex, linkIndex) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === colIndex ? { ...col, links: col.links.filter((_, li) => li !== linkIndex) } : col
      )
    }));
  };

  const updateLink = (colIndex, linkIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === colIndex ? {
          ...col,
          links: col.links.map((link, li) =>
            li === linkIndex ? { ...link, [field]: value } : link
          )
        } : col
      )
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2" data-testid="footer-settings-title">Footer Settings</h1>
            <p className="text-[#6B7280]">Manage footer columns, links, and social media</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            data-testid="save-footer-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Footer Columns */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#111827]">Footer Columns</h2>
            <button
              onClick={addColumn}
              className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2"
              data-testid="add-column"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Column
            </button>
          </div>

          <div className="space-y-4">
            {formData.columns.map((column, colIndex) => (
              <div key={colIndex} className="border-2 border-[#E5E7EB] rounded-lg p-4" data-testid={`column-${colIndex}`}>
                <div className="flex items-center justify-between mb-3">
                  <input
                    type="text"
                    value={column.title}
                    onChange={(e) => updateColumnTitle(colIndex, e.target.value)}
                    className="text-sm font-bold text-[#111827] px-2 py-1 border-2 border-transparent hover:border-[#E5E7EB] focus:border-[#1E40AF] rounded focus:outline-none"
                    placeholder="Column Title"
                    data-testid={`column-title-${colIndex}`}
                  />
                  <button
                    onClick={() => removeColumn(colIndex)}
                    className="text-red-600 hover:text-red-800 text-sm font-semibold"
                    data-testid={`remove-column-${colIndex}`}
                  >
                    Remove Column
                  </button>
                </div>

                <div className="space-y-2 mb-3">
                  {column.links.map((link, linkIndex) => (
                    <div key={linkIndex} className="flex items-center gap-2" data-testid={`link-${colIndex}-${linkIndex}`}>
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => updateLink(colIndex, linkIndex, 'label', e.target.value)}
                        className="flex-1 px-3 py-1.5 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                        placeholder="Link Label"
                      />
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => updateLink(colIndex, linkIndex, 'url', e.target.value)}
                        className="flex-1 px-3 py-1.5 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                        placeholder="URL (e.g., /pages/about)"
                      />
                      <button
                        onClick={() => removeLink(colIndex, linkIndex)}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => addLink(colIndex)}
                  className="text-[#1E40AF] hover:text-[#1e3a8a] text-sm font-semibold flex items-center gap-1"
                  data-testid={`add-link-${colIndex}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Link
                </button>
              </div>
            ))}

            {formData.columns.length === 0 && (
              <p className="text-[#6B7280] text-sm text-center py-4">No columns. Click "Add Column" to create one.</p>
            )}
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <h2 className="text-lg font-bold text-[#111827] mb-4">Social Media Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['facebook', 'instagram', 'linkedin', 'youtube', 'tiktok', 'whatsapp'].map(platform => (
              <div key={platform}>
                <label className="block text-sm font-semibold text-[#111827] mb-2 capitalize">{platform}</label>
                <input
                  type="url"
                  value={formData.social_links[platform] || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, [platform]: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder={`https://${platform}.com/...`}
                  data-testid={`social-${platform}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Copyright & Legal */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <h2 className="text-lg font-bold text-[#111827] mb-4">Copyright & Legal</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Copyright Text</label>
              <input
                type="text"
                value={formData.copyright_text}
                onChange={(e) => setFormData(prev => ({ ...prev, copyright_text: e.target.value }))}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                placeholder="Copyright @2026 All Rights Reserved by UNISAVE"
                data-testid="copyright-text"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Company Text</label>
              <input
                type="text"
                value={formData.company_text}
                onChange={(e) => setFormData(prev => ({ ...prev, company_text: e.target.value }))}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                placeholder="Owned by..."
                data-testid="company-text"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Powered By Text</label>
              <input
                type="text"
                value={formData.powered_by_text}
                onChange={(e) => setFormData(prev => ({ ...prev, powered_by_text: e.target.value }))}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                placeholder="Website powered by..."
                data-testid="powered-by-text"
              />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default FooterSettings;
